import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { breakpoints } from '../../design-system/tokens/breakpoints';

const Caption = styled.p`
  position: absolute;
  z-index: 2;
  right: ${spacing[5]};
  bottom: ${spacing[4]};
  margin: 0;
  max-width: min(42%, 280px);
  text-align: right;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  letter-spacing: ${typography.letterSpacing.wide};
  line-height: ${typography.lineHeight.tight};
  color: ${props => props.theme.colors.textMuted};
  pointer-events: none;
  user-select: none;
  opacity: 0.9;

  @media (max-width: ${breakpoints.md}) {
    display: none;
  }

  @media (prefers-contrast: more) {
    display: none;
  }
`;

export interface MonumentCaptionProps {
  nameEn: string;
  nameGr: string;
  className?: string;
}

function prefersGreek(language: string | undefined): boolean {
  const lang = (language || 'en').toLowerCase();
  return lang === 'gr' || lang.startsWith('gr') || lang.startsWith('el');
}

/** Museum label for watermark headers — shows only the active UI language. */
export const MonumentCaption: React.FC<MonumentCaptionProps> = ({
  nameEn,
  nameGr,
  className,
}) => {
  const { t, i18n } = useTranslation();
  const label = prefersGreek(i18n.language) ? nameGr || nameEn : nameEn || nameGr;

  if (!label) return null;

  return (
    <Caption className={className} aria-label={t('dashboard.monumentMotif')}>
      {label}
    </Caption>
  );
};

export default MonumentCaption;
